import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useHistory } from '@treams/treams-routes';
import { GraphQLError } from 'graphql';

import { Button } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import AwardIcon from 'packages/kakapo-ui-kit/assets/icons/AwardIcon';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import {
	FieldContainer,
	InputResizableElement,
} from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';

import LearningEventAttendeeAward from '../components/LearningEventAttendeeAwardItem';
import CertificatePreviewContainer from '../components/certificates/CertificatePreviewContainer';

function LearningEventAwardCertificatesModal({ event }: { event: graphql.LearningEvent }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const history = useHistory();
	const authenticatedUser = useAuthenticatedUser();

	const [selected, setSelected] = useState<{ [key: string]: boolean }>({});
	const [step, setStep] = useState<number>(0);
	const [loading, setLoading] = useState(false);

	const onSelect = (id: string) => {
		setSelected({
			...selected,
			[id]: !selected[id],
		});
	};

	const [certificateTemplate, setCertificateTemplate] = useState<
		graphql.LearningCertificateTemplate | undefined
	>(event.certificate?.template as graphql.LearningCertificateTemplate);
	const [description, setDescription] = useState<string>(
		event.certificate?.description ? localize(event.certificate.description).text : '',
	);

	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const { data, loading: loadingCertificates } = graphql.useLearningCertificateTemplatesQuery();

	const [updateEvent] = graphql.useChangeLearningEventCertificateMutation();
	const [award] = graphql.useAwardLearningCertificateMutation();

	const options =
		data?.learningCertificateTemplates.edges.map(({ node }) => ({
			label: node.title,
			value: node.id,
			data: node,
		})) ?? [];

	useEffect(() => {
		if (options.length === 1 && !event.certificate?.template) {
			setCertificateTemplate(options[0].data as graphql.LearningCertificateTemplate);
		}
	}, [options, event.certificate?.template]);

	const onSubmit = async () => {
		setLoading(true);

		try {
			await updateEvent({
				variables: {
					input: {
						id: event.id,
						certificate: {
							templateID: certificateTemplate!.id,
							description: [{ languageID: event.languageID, text: description }],
						},
					},
				},
			});

			for (const userID of Object.entries(selected)
				.filter(([key, isSelected]) => isSelected)
				.map(([key]) => key)) {
				await award({
					variables: {
						input: {
							eventID: event.id,
							userID,
						},
					},
				});
			}
		} catch (errors) {
			setError(errors.graphQLErrors[0]);

			return;
		}

		setLoading(false);

		history.push({
			pathname: `/learning/event/${event.id}`,
			state: {
				backgroundLocation: history.location,
			},
		});
	};

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={() => history.goBack()}>
					<Flex alignItems="center" justifyContent="space-between">
						<Flex flexGrow={1} flexBasis={1} justifyContent="center">
							<ModalHeaderTitle>
								{t('learning.event.awardCertificates.modal.title')} ({step + 1} / 2)
							</ModalHeaderTitle>
						</Flex>
					</Flex>
				</ModalHeader>

				<ModalContent>
					<GraphQLErrorMessage error={error} />

					{step === 0 && (
						<>
							{event.attendees.edges.map((edge) => (
								<LearningEventAttendeeAward
									key={edge.node.id}
									user={edge.node}
									disabled={event.awardedCertificates.edges.some(
										({ node }) => node.user.id === edge.node.id,
									)}
									onClick={() => onSelect(edge.node.id)}
									selected={selected[edge.node.id]}
								/>
							))}
						</>
					)}

					{step === 1 && (
						<>
							{options.length > 1 && (
								<FieldContainer>
									<Label
										title={t('learning.event.awardCertificates.modal.certificateTemplate.label')}
									/>

									<Select
										options={options}
										value={options.find((option) => option.value === certificateTemplate?.id)}
										onChange={(value: any) => setCertificateTemplate(value.data)}
										placeholder={t(
											'learning.event.awardCertificates.modal.certificateTemplate.placeholder',
										)}
										noOptionsMessage={() =>
											t('learning.event.awardCertificates.modal.certificateTemplate.noOptions')
										}
										isLoading={loadingCertificates}
										isDisabled={loading}
									/>
								</FieldContainer>
							)}

							<FieldContainer>
								<Label title={t('learning.event.awardCertificates.modal.description.label')} />

								<InputResizableElement
									lineHeight={30}
									minRows={1}
									maxRows={4}
									fullWidth
									value={description}
									onChange={(e) => setDescription(e.currentTarget.value)}
									disabled={loading}
								/>
							</FieldContainer>

							<CertificatePreviewContainer
								attendeeName={`${authenticatedUser.firstName} ${authenticatedUser.lastName}`}
								eventDate={event.startDate}
								eventTitle={localize(event.title).text}
								eventDescription={description}
								accentColor={certificateTemplate?.accentColor ?? undefined}
								logo={certificateTemplate?.logo?.source.url}
								signature={certificateTemplate?.signature?.source.url}
								signerName={certificateTemplate?.signerName ?? undefined}
								signerTitle={certificateTemplate?.signerTitle ?? undefined}
							/>

							<Box mt={3}>
								<HorizontalRule />
							</Box>
						</>
					)}

					<Box mt={3}>
						<Flex justifyContent="space-between" alignItems="center">
							<Button
								type="secondary"
								onClick={() => {
									if (step === 1) {
										setStep(0);

										return;
									}

									history.goBack();
								}}
								disabled={loading}
								title={t('learning.event.awardCertificates.modal.back')}
							/>
							<Button
								icon={<AwardIcon color="white" />}
								onClick={async () => {
									if (step === 0) {
										setStep(1);

										return;
									}

									await onSubmit();
								}}
								disabled={
									!Object.values(selected).some(Boolean) ||
									loading ||
									(step === 1 && !certificateTemplate)
								}
								title={t('learning.event.awardCertificates.modal.confirm')}
							/>
						</Flex>
					</Box>
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}

export default function LearningEventAwardCertificatesModalID({ eventID }: { eventID: string }) {
	const { data } = graphql.useLearningEventQuery({
		variables: {
			id: eventID,
		},
	});

	if (data) {
		return (
			<LearningEventAwardCertificatesModal event={data.learningEvent as graphql.LearningEvent} />
		);
	}

	return (
		<ModalBackground>
			<Modal>
				<Box py={5}>
					<Spinner isLoading size="18px" />
				</Box>
			</Modal>
		</ModalBackground>
	);
}
